import * as React from 'react';

import FormRow from '../../../../../components/FormRow';
import InputHandler from '../../../../../components/InputHandler';
import FormSection from '../../../../../components/FormSection';
import { useIntl } from 'react-intl';
import { ftmtrsPartA, ftmtrsPartC, ftmtrsScore } from 'Routes/Updrs/utils';
import { Container, Item } from 'Components/Grid';
import InfoPopover from '../../../../../components/InfoPopover';
import colors from '../../../../../config/theme/colors';
import { exists } from 'neuro-utils';
import ConfirmationDialog from 'Components/ConfirmationDialog';

const PartBFields = ({ formData, viewing, field, side }: IPartBFields) => {
  const whichHand = formData.document.whichHand;

  const isThisHand = side.toLowerCase() === whichHand?.toLowerCase();

  return whichHand === 'both' || isThisHand ? (
    <Item xs={6}>
      <InputHandler
        type="RadioRow"
        editing={!viewing}
        name={field + side}
        formData={formData}
        options={[0, 1, 2, 3, 4]}
      />
    </Item>
  ) : (
    <></>
  );
};

interface IPartBFields extends IOwnProps {
  field: string;
  side: 'right' | 'left';
}

const PartBScore = ({ formData, viewing, enterScoreManually }: IPartBScore) => {
  const document = formData.document;
  const whichHand = document.whichHand;

  const score = ftmtrsScore<'b'>(document, 'b');

  const rightScore = score && 'right' in score ? (score.right ?? null) : null;

  const leftScore = score && 'left' in score ? (score.left ?? null) : null;

  return (
    <Container style={{ fontWeight: 600 }}>
      {(whichHand === 'both' || whichHand === 'right') && (
        <Item xs={6}>
          {enterScoreManually ? (
            <ManualScoreInput formData={formData} viewing={viewing} fieldNamePart="Bright" />
          ) : (
            (rightScore ?? <InfoPopover text="general.notFilled" color="primary" />)
          )}
        </Item>
      )}
      {(whichHand === 'both' || whichHand === 'left') && (
        <Item xs={6}>
          {enterScoreManually ? (
            <ManualScoreInput formData={formData} viewing={viewing} fieldNamePart="Bleft" />
          ) : (
            (leftScore ?? <InfoPopover text="general.notFilled" color="primary" />)
          )}
        </Item>
      )}
    </Container>
  );
};

interface IPartBScore {
  formData: IFormData<IFTMTRS>;
  viewing: boolean;
  enterScoreManually: boolean;
}

const ManualScoreInput = ({ formData, viewing, fieldNamePart }: IManualScoreInput) => {
  const maxValue = fieldNamePart.substring(0, 1) === 'A' ? 40 : fieldNamePart.substring(0, 1) === 'B' ? 20 : 32;

  return (
    <InputHandler
      type={'NumberField'}
      name={'manualSubtotal' + fieldNamePart}
      editing={!viewing}
      formData={formData}
      min={0}
      max={maxValue}
      placeholder={'updrs.score'}
    />
  );
};

interface IManualScoreInput {
  formData: IFormData<IFTMTRS>;
  viewing: boolean;
  fieldNamePart: 'A' | 'Bright' | 'Bleft' | 'C';
}

const FTMTRSForm = ({ formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const whichHand = formData.document.whichHand;

  const someManualScoresExist =
    exists(formData.document.manualSubtotalA) ||
    exists(formData.document.manualSubtotalBright) ||
    exists(formData.document.manualSubtotalBleft) ||
    exists(formData.document.manualSubtotalC);
  const [enterScoreManually, setEnterScoreManually] = React.useState<boolean>(someManualScoresExist);

  const onChangeManualScoreToggle = (values: TOnChangeValues) => {
    const value = values['enterScoreManually'];
    if (value === false) setEraseConfirmation(true);
    if (value === true) setEnterScoreManually(true);
  };

  const [eraseConfirmation, setEraseConfirmation] = React.useState<boolean>(false);
  const eraseValues = () => {
    formData.onChange?.({
      manualSubtotalA: null,
      manualSubtotalBright: null,
      manualSubtotalBleft: null,
      manualSubtotalC: null,
    });

    setEnterScoreManually(false);
    setEraseConfirmation(false);
  };

  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler
            type="PartialDate"
            editing={!viewing}
            name="date"
            formData={formData}
            dateDefault="now"
            isNotCancellable={true}
          />
        </FormRow>

        <FormRow title="updrs.manualScore">
          <InputHandler
            type="CheckboxSingle"
            editing={!viewing}
            name="enterScoreManually"
            option="true"
            formData={{ document: { enterScoreManually }, onChange: onChangeManualScoreToggle }}
            optionFormatter={o => fm('general.' + o)}
          />
        </FormRow>
      </FormSection>
      <FormSection header="updrs.labels.ftmtrs.partA">
        <>
          {!enterScoreManually &&
            ftmtrsPartA.map(field => (
              <FormRow key={field} title={`updrs.labels.ftmtrs.${field}`}>
                <InputHandler
                  type="RadioRow"
                  editing={!viewing}
                  name={field}
                  formData={formData}
                  options={[0, 1, 2, 3, 4]}
                />
              </FormRow>
            ))}
          <FormRow title="updrs.labels.ftmtrs.subtotalA">
            <div style={{ fontWeight: 600 }}>
              {enterScoreManually ? (
                <ManualScoreInput formData={formData} viewing={viewing} fieldNamePart="A" />
              ) : ftmtrsScore<'a'>(formData.document, 'a') || ftmtrsScore<'a'>(formData.document, 'a') === 0 ? (
                ftmtrsScore<'a'>(formData.document, 'a')
              ) : (
                <InfoPopover text="general.notFilled" color="primary" />
              )}
            </div>
          </FormRow>
        </>
      </FormSection>
      <FormSection header="updrs.labels.ftmtrs.partB">
        <FormRow title={`updrs.labels.ftmtrs.dominantHand`}>
          <InputHandler
            type="Radio"
            editing={!viewing}
            name={'dominantHand'}
            formData={formData}
            options={['right', 'left']}
            optionFormatter={o => fm(`general.${o}`)}
          />
        </FormRow>
        {!enterScoreManually && (
          <FormRow title={`updrs.labels.ftmtrs.handWriting`}>
            <InputHandler
              type="RadioRow"
              editing={!viewing}
              name={'handWriting'}
              formData={formData}
              options={[0, 1, 2, 3, 4]}
            />
          </FormRow>
        )}
        <FormRow title={`updrs.labels.ftmtrs.whichHand`}>
          <InputHandler
            type="Radio"
            editing={!viewing}
            name={'whichHand'}
            formData={formData}
            options={['right', 'left', 'both']}
            optionFormatter={o => fm(`updrs.opts.ftmtrs.whichHand.${o}`)}
          />
        </FormRow>
        <>
          <FormRow>
            <Container style={{ fontWeight: 600, fontSize: '1.8rem', marginBottom: '-2.5rem' }}>
              {(whichHand === 'both' || whichHand === 'right') && <Item xs={6}>{fm('general.right')}</Item>}
              {(whichHand === 'both' || whichHand === 'left') && <Item xs={6}>{fm('general.left')}</Item>}
            </Container>
          </FormRow>
          {!enterScoreManually && (
            <>
              <FormRow title={`updrs.labels.ftmtrs.drawingA`}>
                <Container>
                  <PartBFields formData={formData} viewing={viewing} side="right" field="drawingA" />
                  <PartBFields formData={formData} viewing={viewing} side="left" field="drawingA" />
                </Container>
              </FormRow>
              <FormRow title={`updrs.labels.ftmtrs.drawingB`}>
                <Container>
                  <PartBFields formData={formData} viewing={viewing} side="right" field="drawingB" />
                  <PartBFields formData={formData} viewing={viewing} side="left" field="drawingB" />
                </Container>
              </FormRow>
              <FormRow title={`updrs.labels.ftmtrs.drawingC`}>
                <Container>
                  <PartBFields formData={formData} viewing={viewing} side="right" field="drawingC" />
                  <PartBFields formData={formData} viewing={viewing} side="left" field="drawingC" />
                </Container>
              </FormRow>
              <FormRow title={`updrs.labels.ftmtrs.pouring`}>
                <Container>
                  <PartBFields formData={formData} viewing={viewing} side="right" field="pouring" />
                  <PartBFields formData={formData} viewing={viewing} side="left" field="pouring" />
                </Container>
              </FormRow>
            </>
          )}
        </>
        <FormRow title="updrs.labels.ftmtrs.subtotalB">
          <PartBScore formData={formData} viewing={viewing} enterScoreManually={enterScoreManually} />
        </FormRow>
      </FormSection>
      <FormSection header="updrs.labels.ftmtrs.partC">
        <>
          {!enterScoreManually &&
            ftmtrsPartC.map(field => (
              <FormRow key={field} title={`updrs.labels.ftmtrs.${field}`}>
                <InputHandler
                  type="RadioRow"
                  editing={!viewing}
                  name={field}
                  formData={formData}
                  options={[0, 1, 2, 3, 4]}
                />
              </FormRow>
            ))}
          <FormRow title="updrs.labels.ftmtrs.subtotalC">
            <div style={{ fontWeight: 600 }}>
              {enterScoreManually ? (
                <ManualScoreInput formData={formData} viewing={viewing} fieldNamePart="C" />
              ) : ftmtrsScore<'c'>(formData.document, 'c') || ftmtrsScore<'c'>(formData.document, 'c') === 0 ? (
                ftmtrsScore<'c'>(formData.document, 'c')
              ) : (
                <InfoPopover text="general.notFilled" color="primary" />
              )}
            </div>
          </FormRow>
        </>
      </FormSection>
      <span style={{ fontStyle: 'italic', color: colors.darkGray }}>{fm('updrs.labels.ftmtrs.disclaimer')}</span>
      <ConfirmationDialog
        open={eraseConfirmation}
        text={fm('updrs.manuallyEnteredScoresWillBeDeleted')}
        cancel={{ callback: () => setEraseConfirmation(false) }}
        confirm={{ callback: () => eraseValues() }}
      />
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IFTMTRS>;
  viewing: boolean;
}

export default FTMTRSForm;
